import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { setToken } from "utils/auth";
import { buildApiUrl } from "utils/api";
import { useUser } from "contexts/UserContext";
import { resolvePortalPath } from "utils/portalPath";
import mellbizzLogo from "assets/img/auth/Mellbizz-logo.png";

export default function SignIn() {
  const navigate = useNavigate();
  const { setUserFromApi } = useUser();
  const [formData, setFormData] = useState({ identifier: "", password: "" });
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState({ show: false, message: "", type: "error" });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    if (toast.show) {
      const timer = setTimeout(() => {
        setToast({ show: false, message: "", type: "error" });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [toast.show]);

  const normalizePhoneIdentifier = (value = "") => {
    const trimmed = value.trim();
    const normalized = trimmed.replace(/[\s\-()]/g, "");
    if (!normalized) return "";
    return normalized.startsWith("+")
      ? `+${normalized.slice(1).replace(/\D/g, "")}`
      : normalized.replace(/\D/g, "");
  };

  const getLoginIdentifier = (value = "") => {
    const normalized = normalizePhoneIdentifier(value);
    const isIndianPhone = /^(?:\+91|91|0)?[6-9]\d{9}$/.test(normalized);
    return isIndianPhone ? normalized : value.trim();
  };

  const validate = () => {
    const errs = {};
    const trimmedIdentifier = formData.identifier.trim();

    // Check if identifier is empty
    if (!trimmedIdentifier) {
      errs.identifier = "Email or Phone Number is required";
    } else {
      // Check if it's a valid email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isEmail = emailRegex.test(trimmedIdentifier);

      // Normalize identifier for phone checks: remove spaces, dashes, parentheses
      const normalized = trimmedIdentifier.replace(/[\s\-()]/g, "");
      const digitsOnly = normalized.startsWith("+")
        ? `+${normalized.slice(1).replace(/\D/g, "")}`
        : normalized.replace(/\D/g, "");

      // Check if it's a valid Indian phone number
      // Accept formats like: 9876543210, 09876543210, 919876543210, +919876543210, +91 9876543210
      const isPhone = /^(?:\+91|91|0)?[6-9]\d{9}$/.test(digitsOnly);

      // Must be either valid email or valid phone
      if (!isEmail && !isPhone) {
        errs.identifier = "Enter a valid email address or phone number";
      }
    }

    if (!formData.password.trim()) {
      errs.password = "Password is required";
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
    setErrors((prev) => ({ ...prev, [id]: undefined }));
  };

  const handleForgotPassword = () => {
    // Navigate to forgot password page
    navigate("/auth/forgot-password");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    try {
      const res = await fetch(buildApiUrl("/auth/login"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identifier: getLoginIdentifier(formData.identifier),
          password: formData.password,
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (Array.isArray(data.errors) && data.errors.length) {
          const fieldErrors = {};
          data.errors.forEach((errObj) => {
            if (errObj.path) {
              // Map backend field name to identifier for consistency
              const fieldName = errObj.path === 'phone' || errObj.path === 'email' ? 'identifier' : errObj.path;
              fieldErrors[fieldName] = errObj.msg || data.message;
            }
          });
          setErrors(fieldErrors);
          if (data.message) {
            setToast({ show: true, message: data.message, type: "error" });
          }
        } else {
          setToast({ show: true, message: data?.message || "Login failed", type: "error" });
        }
        return;
      }
      const token = data.token || data.data?.token;
      if (token) {
        setToken(token);
        
        // Store user data and permissions in context (normalizes permissions)
        try {
          setUserFromApi(data);
        } catch (e) {
          // fallback: no-op
        }

        const responseUser = data.user || data.data?.user || data.data || data;
        const userRole = responseUser?.role;
        navigate(resolvePortalPath("/dashboard", userRole), { replace: true });
      } else {
        console.warn("login succeeded without a token", data);
        setToast({
          show: true,
          message:
            data.message ||
            "Authentication succeeded but no token received. Please try again.",
          type: "error",
        });
      }
    } catch (err) {
      console.error("SignIn network error:", err);
      const message = err?.message?.includes("Failed to fetch")
        ? "Network error. Please check your internet connection or API configuration."
        : err?.message || "Network error. Please try again.";
      setToast({ show: true, message, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 overflow-auto">
      {/* Toast Notification */}
      {toast.show && (
        <div
          className={`fixed top-6 left-1/2 z-50 w-full max-w-md -translate-x-1/2 transform rounded-2xl p-4 text-center text-sm font-medium text-white shadow-2xl backdrop-blur-md transition-all duration-300 ${toast.type === "success"
            ? "border border-green-400 bg-green-500/90"
            : "border border-red-400 bg-red-500/90"
            }`}
        >
          {toast.message}
        </div>
      )}

      {/* Background Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-gradient-to-br from-indigo-200 to-purple-200 blur-3xl opacity-20 dark:from-indigo-900/20 dark:to-purple-900/20"></div>
        <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-gradient-to-tr from-blue-200 to-cyan-200 blur-3xl opacity-20 dark:from-blue-900/20 dark:to-cyan-900/20"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-80 w-80 rounded-full bg-gradient-to-br from-purple-200 to-indigo-200 blur-3xl opacity-10 dark:from-purple-900/10 dark:to-indigo-900/10"></div>
      </div>

      {/* Main Content */}
      <div className="relative flex-1 flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8 overflow-y-auto py-6 min-h-0">
        {/* Container */}
        <div className="w-full max-w-md space-y-8 py-2">
          {/* Logo Section */}
          <div className="flex flex-col items-center space-y-4">
            <div className="flex items-center justify-center h-16 w-16 rounded-2xl bg-gradient-to-br from-indigo-600 to-purple-600 shadow-lg">
              <img
                src={mellbizzLogo}
                alt="Mellbizz"
                className="h-13 w-auto object-contain"
              />
            </div>
            <div className="text-center">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                Mellbizz
              </h1>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                Business Management Platform
              </p>
            </div>
          </div>

          {/* Form Card */}
          <div className="relative rounded-3xl border border-gray-200/50 dark:border-slate-700/50 bg-white/70 dark:bg-slate-800/70 backdrop-blur-xl shadow-2xl p-8 sm:p-10 space-y-6">
            {/* Welcome Text */}
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Welcome Back
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Enter your details to access your dashboard
              </p>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Email or Phone Number Input */}
              <div className="space-y-2">
                <label className="block text-sm font-semibold text-gray-900 dark:text-white">
                  <span className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    Email or Phone Number
                  </span>
                </label>
                <input
                  type="text"
                  id="identifier"
                  placeholder="Enter your email address or phone number"
                  value={formData.identifier}
                  onChange={handleChange}
                  disabled={isLoading}
                  autoComplete="username"
                  className={`w-full rounded-xl border-2 bg-white/50 dark:bg-slate-900/30 px-4 py-3 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${errors.identifier
                    ? "border-red-400 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-200 dark:border-red-500 dark:focus:ring-red-900/30"
                    : "border-gray-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500 focus:border-indigo-500 dark:focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-900/30"
                    }`}
                />
                {errors.identifier && (
                  <p className="text-sm font-medium text-red-500 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.identifier}
                  </p>
                )}
              </div>

              {/* Password Input */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="block text-sm font-semibold text-gray-900 dark:text-white">
                    <span className="flex items-center gap-2">
                      <svg className="w-5 h-5 text-indigo-600 dark:text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      Password
                    </span>
                  </label>
                  <button
                    type="button"
                    onClick={handleForgotPassword}
                    className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors"
                  >
                    Forgot?
                  </button>
                </div>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    placeholder="Enter your password"
                    value={formData.password}
                    onChange={handleChange}
                    disabled={isLoading}
                    className={`w-full rounded-xl border-2 bg-white/50 dark:bg-slate-900/30 px-4 py-3 pr-10 text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${errors.password
                      ? "border-red-400 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-200 dark:border-red-500 dark:focus:ring-red-900/30"
                      : "border-gray-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500 focus:border-indigo-500 dark:focus:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-900/30"
                      }`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
                    disabled={isLoading}
                  >
                    {showPassword ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-4.803m5.596-3.856a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0M15 12a3 3 0 11-6 0 3 3 0 016 0zm6 0c0 1.657-.672 3.157-1.757 4.243A6 6 0 0121 12a6 6 0 00-9-5.656" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-sm font-medium text-red-500 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.password}
                  </p>
                )}
              </div>

              {/* Sign In Button */}
              <button
                type="submit"
                disabled={isLoading}
                className="mt-6 w-full rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 py-3 px-4 text-base font-semibold text-white shadow-lg transition-all duration-300 hover:shadow-2xl hover:from-indigo-700 hover:to-purple-700 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed disabled:shadow-md dark:from-indigo-500 dark:to-purple-500 dark:hover:from-indigo-600 dark:hover:to-purple-600 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <svg
                      className="animate-spin h-5 w-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Signing in...
                  </>
                ) : (
                  <>
                    <span>Sign In</span>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </>
                )}
              </button>

              {/* Terms and Privacy */}
              <p className="text-center text-xs text-gray-600 dark:text-gray-400 pt-3">
                By signing in, you agree to our{" "}
                <a href="https://www.mellbizz.in/legal-links/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="font-semibold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors underline">
                  Terms of Service
                </a>
                {" "}and{" "}
                <a href="https://www.mellbizz.in/legal-links/privacy-policy" target="_blank" rel="noopener noreferrer" className="font-semibold text-indigo-600 hover:text-indigo-700 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors underline">
                  Privacy Policy
                </a>
              </p>
            </form>
          </div>

          {/* Info Cards */}
          <div className="grid grid-cols-3 gap-3 pt-2">
            {[
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                ),
                label: "Analytics",
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-2a6 6 0 0112 0v2zm0 0h6v-2a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ),
                label: "Connections",
              },
              {
                icon: (
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                ),
                label: "Growth",
              },
            ].map((item, idx) => (
              <div
                key={idx}
                className="flex flex-col items-center justify-center rounded-xl border border-gray-200/50 dark:border-slate-700/50 bg-white/40 dark:bg-slate-800/40 backdrop-blur-sm p-3 hover:bg-white/60 dark:hover:bg-slate-800/60 transition-all duration-200"
              >
                <div className="text-indigo-600 dark:text-indigo-400 mb-2">{item.icon}</div>
                <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 text-center">
                  {item.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Footer - Integrated */}
      <div className="relative border-t border-gray-200/30 dark:border-slate-700/30 bg-white/30 dark:bg-slate-800/30 backdrop-blur-sm flex items-center justify-center py-3">
        <div className="text-center text-xs text-gray-600 dark:text-gray-400">
          <p>&copy; 2026 Mellbizz. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}
